import { useState, useEffect } from 'react';

export default function Hero() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const reveal = (delay: number) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'none' : 'translateY(40px)',
    transition: `opacity 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
  });

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-16 sm:py-20"
      style={{ background: '#FDFAF4' }}>

      {/* Subtle aged corner ornaments */}
      <div className="absolute top-6 left-6 w-16 h-16 border-t border-l border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute top-6 right-6 w-16 h-16 border-t border-r border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute bottom-6 left-6 w-16 h-16 border-b border-l border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute bottom-6 right-6 w-16 h-16 border-b border-r border-[#A8323C]/30 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-28 items-center justify-center">

          {/* Text block */}
          <div style={reveal(100)} className="text-center lg:text-left space-y-6 sm:space-y-8 order-2 lg:order-1">

            {/* Ornamental top line */}
            <div className="flex items-center gap-3 justify-center lg:justify-start">
              <div className="h-px flex-1 max-w-[60px] bg-[#A8323C]/40" />
              <span className="text-[#A8323C]/40 text-lg font-serif select-none">✦</span>
              <div className="h-px flex-1 max-w-[60px] bg-[#A8323C]/40" />
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-serif text-[#2A1A14] leading-none italic">
                Сергей
              </h1>
              {/* Ampersand ornament */}
              <div className="flex items-center gap-4 justify-center lg:justify-start py-1">
                <div className="h-px w-12 sm:w-16 bg-[#A8323C]/40" />
                <span className="text-3xl sm:text-4xl font-serif italic text-[#A8323C]">&amp;</span>
                <div className="h-px w-12 sm:w-16 bg-[#A8323C]/40" />
              </div>
              <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-serif text-[#2A1A14] leading-none italic">
                Екатерина
              </h1>
            </div>

            {/* Location */}
            <div>
              <p className="text-xs sm:text-sm tracking-[0.35em] uppercase text-[#8B6B5A] font-mono block">
                Москва
              </p>
            </div>

            <div className="pt-2 sm:pt-4">
              <div className="inline-flex items-center gap-3 text-sm tracking-wider text-[#8B6B5A] animate-bounce px-3 py-1">
                <span className="font-mono text-xs">Листайте вниз</span>
                <i className="ri-arrow-down-line text-base"></i>
              </div>
            </div>
          </div>

          {/* Photo block — Vintage frame */}
          <div style={reveal(350)} className="order-1 lg:order-2 flex-shrink-0">
            <div className="relative" style={{ transform: 'rotate(1.5deg)' }}>

              {/* Outer aged mat border */}
              <div className="relative w-[280px] sm:w-[340px] lg:w-[380px]"
                style={{
                  background: '#EDE5D8',
                  padding: '16px',
                  boxShadow: '0 4px 20px rgba(42,26,20,0.2), inset 0 0 0 1px rgba(168,50,60,0.12)',
                }}
              >
                {/* Double inner border */}
                <div className="absolute inset-[8px] pointer-events-none z-10"
                  style={{ border: '1px solid rgba(168,50,60,0.35)' }} />
                <div className="absolute inset-[11px] pointer-events-none z-10"
                  style={{ border: '1px solid rgba(168,50,60,0.15)' }} />

                {/* Corner ornaments */}
                {[
                  'top-[4px] left-[4px]',
                  'top-[4px] right-[4px]',
                  'bottom-[4px] left-[4px]',
                  'bottom-[4px] right-[4px]',
                ].map((pos, i) => (
                  <div key={i} className={`absolute ${pos} w-7 h-7 z-20 flex items-center justify-center`}>
                    <span className="font-serif leading-none select-none text-[22px]"
                      style={{ color: '#A8323C', opacity: 0.55 }}>
                      ✦
                    </span>
                  </div>
                ))}

                {/* Photo with vintage filter */}
                <div className="w-full aspect-[3/4] overflow-hidden relative">
                  <img
                    src="/images/couple.jpeg"                    
                    alt="Couple"
                    className="w-full h-full object-cover object-top"
                    style={{
                      filter: 'sepia(0.12) brightness(0.97) contrast(1.06) saturate(0.92)',
                    }}
                  />
                  {/* Vignette */}
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at center, transparent 45%, rgba(60,30,15,0.3) 100%)' }} />
                </div>

              </div>

              {/* Tape strip */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 h-7 w-16 z-30"
                style={{
                  background: 'rgba(245,240,232,0.85)',
                  transform: 'translateX(-50%) rotate(-2deg)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                  borderLeft: '1px solid rgba(168,50,60,0.1)',
                  borderRight: '1px solid rgba(168,50,60,0.1)',
                }}
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
